import { useState } from "react";
import { Business, BusinessSsoOidcDto, BusinessSsoSamlDto, LensSDKException, SSOType } from "lens-platform-sdk";
import { ButtonBar } from "@k8slens/lds";
import { FormField } from "@k8slens/lds-form";
import { ssoTypeOptions, SSOTypeOption, useBusinessSSO } from "src/hooks/useBusinessSSO";
import { getACSUrl, getSamlSSOEntityID } from "src/services/sso";

import Notification from "src/components/Notification/Notification";
import { CopyToClipboard } from "src/components/CopyToClipboard/CopyToClipboard";
import TrackedButton from "src/components/Button/TrackedButton";
import { SamlSSOProviderForm } from "src/components/SamlSSOForm/SamlSSOProviderForm";
import { OIDCSSOProviderForm } from "src/components/OIDCSSOForm/OIDCSSOProviderForm";
import { RadioGroup, RadioGroupOption } from "src/components/RadioGroup/RadioGroup";

import { BusinessSSOInfo } from "./BusinessSSOInfo";

import styles from "./BusinessSSO.module.css";
import { useConfirmDialog } from "src/components/ConfirmDialog/ConfirmDialog";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";

export const BusinessSSO = ({ business }: { business: Business | undefined | null }) => {
  const businessId = business?.id;
  const {
    businessSSO,
    createBusinessSSO,
    creatingBusinessSSO,
    deleteBusinessSSO,
    deletingBusinessSSO,
    errorCreatingBusinessSSO,
  } = useBusinessSSO(businessId);
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const [sendSSOEmailLoading, setSendSSOEmailLoading] = useState(false);
  const [sendSSOEmailError, setSendSSOEmailError] = useState<LensSDKException | null>(null);
  const platformClient = useLensPlatformClient();

  const handleRemoveSSO = () => {
    if (!businessId) {
      return;
    }

    deleteBusinessSSO(businessId);
  };

  const handleSendSSOActivationEmail = async () => {
    setSendSSOEmailError(null);

    const confirmed = await confirm("SSO activation email", "Send SSO activation email to all users in the Business?");

    if (confirmed && businessId) {
      try {
        setSendSSOEmailLoading(true);

        await platformClient.business.sendBusinessSSOActivationEmail(businessId);
      } catch (error) {
        if (error instanceof LensSDKException) {
          setSendSSOEmailError(error);
        }
      } finally {
        setSendSSOEmailLoading(false);
      }
    }
  };

  const [selectedSSOType, setSelectedSSOType] = useState(ssoTypeOptions[0]);

  const onChangeSSOType = (option: SSOTypeOption | undefined) => {
    if (option) {
      setSelectedSSOType(option);
    }
  };

  const acsUrl = getACSUrl(businessId);
  const acsUrlLabel = selectedSSOType.id === SSOType.OIDC ? "Redirect URI" : "Assertion Consumer Service URL";
  const entityID = getSamlSSOEntityID(businessId);

  const addBusinessSSO = (settings: BusinessSsoSamlDto | BusinessSsoOidcDto) => {
    if (!businessId) {
      return;
    }

    createBusinessSSO({
      businessId,
      ssoSettings: {
        config: settings,
      },
    });
  };

  return (
    <div className={styles.sso}>
      {errorCreatingBusinessSSO && (
        <Notification level="error" message={errorCreatingBusinessSSO.message} type="closable" />
      )}
      {sendSSOEmailError && <Notification level="error" message={sendSSOEmailError.message} type="closable" />}
      {businessSSO && <BusinessSSOInfo business={business} ssoInfo={businessSSO} />}
      {businessSSO && (
        <section>
          <p>
            Let users of your Business ID know that a Single Sign On has been activated by sending automatic email with
            SSO activation link.
          </p>
          <ButtonBar type="stacked" className={styles.buttonBar}>
            <TrackedButton
              label="Send SSO activation email"
              onClick={handleSendSSOActivationEmail}
              primary
              loading={sendSSOEmailLoading}
              textTransform="capitalize"
            />
            <TrackedButton
              label="Clear single sign-on settings"
              onClick={handleRemoveSSO}
              discreet
              buttonType="reset"
              loading={deletingBusinessSSO}
              textTransform="capitalize"
            />
          </ButtonBar>
          {ConfirmDialog}
        </section>
      )}
      {!businessSSO && acsUrl && entityID && (
        <div>
          <div className={styles.ssoInfoSection}>
            <dl className={styles.ssoInfo}>
              <dt aria-label={acsUrlLabel}>
                <strong>{`${acsUrlLabel}:`}</strong>
              </dt>
              <dd aria-label={acsUrl} data-testid="acsUrl">
                <span className={styles.ssoInfoText} title={acsUrl}>
                  {acsUrl}
                </span>
                <CopyToClipboard text={acsUrl} />
              </dd>
              {selectedSSOType.id === SSOType.SAML ? (
                <dt aria-label="Service Provider Entity ID">
                  <strong>Service Provider Entity ID:</strong>
                </dt>
              ) : null}
              {selectedSSOType.id === SSOType.SAML ? (
                <dd aria-label={entityID} data-testid="entityID">
                  <span className={styles.ssoInfoText} title={entityID}>
                    {entityID}
                  </span>
                  <CopyToClipboard text={entityID} />
                </dd>
              ) : null}
            </dl>
          </div>
          <div>
            <FormField
              id="ssoProviderType"
              errorId="ssoProviderTypeError"
              labelId="ssoProviderTypeLabel"
              label="Select SSO provider type"
            >
              <RadioGroup className={styles.ssoTypeSelection} value={selectedSSOType} onChange={onChangeSSOType}>
                {ssoTypeOptions.map((option) => (
                  <RadioGroupOption key={option.id} value={option}>
                    <span className="uppercase">{option.label}</span>
                  </RadioGroupOption>
                ))}
              </RadioGroup>
            </FormField>
          </div>
          {selectedSSOType.id === SSOType.SAML ? (
            <SamlSSOProviderForm loading={creatingBusinessSSO} onSubmit={addBusinessSSO} />
          ) : (
            <OIDCSSOProviderForm loading={creatingBusinessSSO} onSubmit={addBusinessSSO} />
          )}
        </div>
      )}
    </div>
  );
};
